import React, { useEffect, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { makeStyles } from 'tss-react/mui'
import { useShop } from 'src/Libs/shop/hooks'
import ShopProductCategoryCard from '../ShopProductCategory/ShopProductCategoryCard'
import { CrossSellingProductsResponse } from '../../models/cross-selling-products.model'
import NoImage from 'src/Resources/Images/shop/NoImage.png'
import { ProductType } from '../../types'
import { Box } from '@mui/material'
import { Loading } from '@e3dc-react/shell/Elements/Loading'
import { useShopCartUpdater } from '../../hooks/use-shop-cart-updater'

interface CrossSellingProductsProps {
  productId: string
}

const useStyles = makeStyles()(theme => ({
  swiperItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  moreProductsContainer: {
    minWidth: '270px',
    width: '100%',
    '--swiper-theme-color': theme.palette.grey.grey09,
    '.swiper-pagination-bullet-active': {
      transform: 'scale(1.5)',
    },
    '.swiper-slide': {
      display: 'flex',
    },
    '.swiper-button-next': {
      backgroundColor: theme.palette.grey.grey00,
      padding: theme.spacing(4),
      borderRadius: '50%',
      '&::after': {
        content: "'next'",
        fontSize: '18px',
      },
    },
    '.swiper-button-prev': {
      backgroundColor: theme.palette.grey.grey00,
      padding: theme.spacing(4),
      borderRadius: '50%',
      '&::after': {
        content: "'prev'",
        fontSize: '18px',
      },
    },
  },
  card: { maxWidth: '270px', paddingTop: theme.spacing(2), paddingBottom: theme.spacing(2) },
  paperContent: {
    maxWidth: 'calc(100vw - 310px)',
    marginTop: theme.spacing(6),
    [theme.breakpoints.down('md')]: {
      maxWidth: 'calc(100vw - 40px)',
      paddingRight: theme.spacing(3),
    },
    [theme.breakpoints.only('xs')]: {
      minWidth: '100%',

      marginRight: 0,
    },
  },
}))

/*
 * This Component is showed on Product Component (Component that shows Detailed Information about Product), which shows more related Similar Products as the one you are currently viewing.
 * NOTE: This Component is not showed up everytime , just when we have CrossSelling Produt ID from the API.
 */

const CrossSellingProducts: React.FC<CrossSellingProductsProps> = ({ productId }) => {
  const { getCrossSellingProducts } = useShop()
  const [productsData, setProductsData] = useState<CrossSellingProductsResponse[] | null>()
  const [isLoading, setIsLoading] = useState(false)
  const { addProductToCart, updateProductToCart, deleteProductFromCart, isLoading: isCartLoading } = useShopCartUpdater()
  const { classes } = useStyles()

  useEffect(() => {
    setIsLoading(true)
    getCrossSellingProducts(productId)
      .then(result => {
        setProductsData(result)
      })
      .catch(error => console.log(error))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*TODO: Add Theme Breakpoints */
  const sliderBreakpoints = {
    0: {
      slidesPerView: 1,
    },
    700: {
      slidesPerView: 2,
    },
    1300: {
      slidesPerView: 3,
    },
    1700: {
      slidesPerView: 4,
    },
    2300: {
      slidesPerView: 5,
    },
  }

  if (isLoading) {
    return (
      <Box sx={{ marginTop: 6 }}>
        <Loading />
      </Box>
    )
  }

  return (
    <Box>
      {productsData?.map((crossSelling, index) => (
        <Paper key={index} title={crossSelling.crossSelling?.translated.name} className={classes.paperContent}>
          <Swiper
            pagination={{
              clickable: true,
            }}
            loop={true}
            slidesPerView={4}
            spaceBetween={0}
            navigation={true}
            modules={[Navigation]}
            className={classes.moreProductsContainer}
            breakpoints={sliderBreakpoints}
          >
            {crossSelling.products.map(product => {
              const productAvailability = product.extensions.e3dcAvailability
              const customFields = product.translated.customFields
              const productHint = customFields.e3dcPopupActive && customFields.e3dcPopupInfoText ? customFields.e3dcPopupInfoText : false
              const isProductHighlighted = product.markAsTopseller
              const isPriceApplied = Boolean(product.extensions?.e3dcPriceApplied?.state)
              return (
                <SwiperSlide key={product.id} className={classes.swiperItem}>
                  <div className={classes.card}>
                    <ShopProductCategoryCard
                      key={product.id}
                      productId={product.id}
                      title={product.translated.name}
                      image={product.cover?.media != null ? product?.cover?.media.url : NoImage}
                      price={product.calculatedPrice?.listPrice?.price || product.calculatedPrice.unitPrice}
                      unitPrice={product.calculatedPrice.unitPrice}
                      priceApplied={isPriceApplied}
                      productType={product.extensions.e3dcData.customFields?.productType ?? ProductType.Normal}
                      productRequiresAdditionalProducts={product.extensions.e3dcData.requiresAdditionalProducts}
                      minQtyOfAdditionalProducts={product.extensions.e3dcData.minQtyOfAdditionalProducts}
                      powerMeterType={product.extensions.e3dcData.customFields?.powerMeterType ?? undefined}
                      productAvailability={productAvailability}
                      handleProductUpdate={updateProductToCart}
                      handleProductAdd={addProductToCart}
                      handleProductDelete={deleteProductFromCart}
                      isChangeLoading={isCartLoading}
                      productHint={productHint}
                      isProductHighlight={isProductHighlighted}
                    />
                  </div>
                </SwiperSlide>
              )
            })}
          </Swiper>
        </Paper>
      ))}
    </Box>
  )
}

export default CrossSellingProducts
